/**
 * MouldSearchSelect — shared type-to-search mould picker.
 *
 * Used by:
 *   • RM Repository → Add INP from mould
 *   • Convert Category → INP dialog (Feb 2026)
 *
 * Contract:
 *   • Debounced (300ms) fetch of `/api/moulds?search=<term>&status=ELIGIBLE`
 *     — `ELIGIBLE` is a subset of what `services/mould_resolver` accepts,
 *     so a mould visible in this list is guaranteed to be resolvable
 *     downstream (fixes the "picked it from the list and it still
 *     failed" class of bugs).
 *   • Selection is committed ONLY on explicit click of an option.
 *     There is no free-text fallback and no on-blur commit — a typed
 *     mould code cannot be submitted; the operator must pick a row.
 *   • On commit, invokes `onSelect(mould)` with the whole document.
 *     Parent stores `mould.id` (uuid) — the resolver's fast path.
 *   • Renders a summary card with a Change button once picked.
 *
 * Props:
 *   value           — the currently-selected mould object, or null
 *   onSelect(m)     — called with the picked mould
 *   onClear()       — called when the operator clicks Change
 *   testIdPrefix    — data-testid prefix; defaults to "mould-search"
 *   placeholder     — search input placeholder
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MouldSearchSelect = ({
  value,
  onSelect,
  onClear,
  testIdPrefix = "mould-search",
  placeholder = "Type 3+ characters of the mould code or part name…",
  label = "Mould Code",
  required = true,
  getHeaders,
}) => {
  const [term, setTerm] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (value) return;
    const t = term.trim();
    if (t.length < 1) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const h = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/moulds`, {
          params: { search: t, status: "ELIGIBLE" },
          headers: getHeaders ? getHeaders() : undefined,
        });
        if (!cancelled) setOptions(res.data || []);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(h); };
  }, [term, value, getHeaders]);

  if (value) {
    return (
      <div className="space-y-2">
        <Label>
          {label}
          {required && <span className="text-red-600"> *</span>}
        </Label>
        <div
          className="border-2 border-emerald-200 bg-emerald-50 rounded-md p-3"
          data-testid={`${testIdPrefix}-selected-summary`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-1">
              <div className="font-mono font-bold text-base">
                {value.mould_code}
              </div>
              <div className="text-xs text-stone-700">
                <b>Part:</b> {value.part_name || "—"} •{" "}
                <b>Model:</b> {value.model_name || "—"}
              </div>
              <div className="text-xs text-stone-700">
                <b>Weight:</b> {value.per_unit_weight_g || "?"} g •{" "}
                <b>MB ratio:</b> {value.mb_ratio_pct || 0}%
              </div>
              <div className="text-xs text-stone-700">
                <b>Polymer blend:</b>{" "}
                {(value.polymer_components || []).length > 0
                  ? value.polymer_components
                      .map((p) => `${p.polymer_rm_id}@${p.percentage}%`)
                      .join(" + ")
                  : value.polymer_rm_id || "—"}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { onClear && onClear(); setTerm(""); }}
              data-testid={`${testIdPrefix}-change-btn`}
            >
              Change
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required && <span className="text-red-600"> *</span>}
      </Label>
      <Input
        data-testid={`${testIdPrefix}-input`}
        placeholder={placeholder}
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        autoFocus
      />
      {loading && <p className="text-xs text-stone-500">Searching…</p>}
      {!loading && term.length >= 1 && options.length === 0 && (
        <p className="text-xs text-stone-500">
          No moulds match — try a different term.
        </p>
      )}
      {options.length > 0 && (
        <div
          className="border rounded-md max-h-48 overflow-y-auto"
          data-testid={`${testIdPrefix}-results`}
        >
          {options.map((m) => (
            <button
              key={m.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-stone-100 border-b last:border-b-0 text-sm"
              onClick={() => { onSelect(m); setTerm(""); }}
              data-testid={`${testIdPrefix}-option-${m.mould_code}`}
            >
              <div className="font-mono font-medium">{m.mould_code}</div>
              <div className="text-[11px] text-stone-500">
                {m.part_name || "—"} • {m.model_name || "—"} •{" "}
                {m.per_unit_weight_g || "?"}g
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MouldSearchSelect;
